// Trustpilot product reviews, served by the YChroma reviews backend through the Shopify App Proxy.
// The request is deliberately the last thing the page does: it waits for `load` and then for an idle frame.

const REVIEWS_BASE = '/apps/trustpilot/reviews';
const REQUEST_TIMEOUT = 8000;
const RETRY_DELAY = 1500;
const MAX_COUNT_SKUS = 250;
// The newest reviews shown as cards.
const CARDS_SHOWN = 3;

/**
 * @typedef {Object} Review
 * @property {string} id
 * @property {string} [sku]
 * @property {string} [title]
 * @property {string} text
 * @property {{ name: string | null }} author
 * @property {string | null} createdAt
 */

class RequestError extends Error {
  /**
   * @param {string} message
   * @param {number} status
   * @param {string} code
   */
  constructor(message, status, code) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

/**
 * @param {string} url
 * @param {AbortSignal} signal
 */
async function getJson(url, signal) {
  const res = await fetch(url, { headers: { Accept: 'application/json' }, signal });
  let body = null;
  try {
    body = await res.json();
  } catch {
    // Non-JSON error (e.g. request didn't go through the App Proxy).
  }
  if (!res.ok) {
    throw new RequestError(body?.error?.message || `Request failed (${res.status})`, res.status, body?.error?.code || 'UNKNOWN');
  }
  return body;
}

/** Resolves once the page (images, scripts, iframes) has finished loading and the main thread is idle. */
function afterPageLoad() {
  return new Promise((resolve) => {
    const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(() => resolve(undefined), { timeout: 2000 }) : setTimeout(resolve, 1));
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  });
}

/** @param {number} ms */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** @param {string | null} iso */
function timeAgo(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';

  const days = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (days < 0) return '';
  if (days < 1) return 'Today';
  if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

/** @param {string} url */
async function request(url) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
  try {
    return await getJson(url, controller.signal);
  } finally {
    clearTimeout(timeout);
  }
}

/** @type {Map<string, Promise<any>>} */
const sharedRequests = new Map();

/**
 * If more than one <product-reviews> is on the page, they share one request per URL.
 * Retries once for temporary upstream failures only.
 * @param {string} url
 */
function fetchShared(url) {
  let promise = sharedRequests.get(url);
  if (!promise) {
    promise = request(url).catch(async (error) => {
      if (!(error instanceof RequestError) || (error.status !== 502 && error.status !== 503)) throw error;
      await wait(RETRY_DELAY);
      return request(url);
    });
    sharedRequests.set(url, promise);
  }
  return promise;
}

/**
 * Product-level count and average, shared by the header count and the review list summary (one request, logged once).
 * @param {string[]} skus
 */
function fetchProductCount(skus) {
  const url = `${REVIEWS_BASE}/product-count?skus=${skus.map(encodeURIComponent).join(',')}`;
  const isFirst = !sharedRequests.has(url);
  const promise = fetchShared(url);
  if (isFirst) {
    promise.catch((error) => {
      if (error instanceof RequestError) console.warn('[Trustpilot count]', error.code);
      else console.warn('[Trustpilot count]', error);
    });
  }
  return promise;
}

/**
 * The product's newest reviews for the cards. The service reviews fallback reads the same request,
 * so both agree on whether the product has enough reviews to show.
 * @param {string[]} skus
 */
function fetchProductCards(skus) {
  const params = new URLSearchParams({ skus: skus.join(','), page: '1', perPage: String(CARDS_SHOWN) });
  return fetchShared(`${REVIEWS_BASE}/product-all?${params}`);
}

/**
 * "April 28", with the year added when it isn't the current one.
 * @param {string | null} iso
 */
function formatDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';
  /** @type {Intl.DateTimeFormatOptions} */
  const options = { month: 'long', day: 'numeric' };
  if (date.getFullYear() !== new Date().getFullYear()) options.year = 'numeric';
  return date.toLocaleDateString('en-US', options);
}

class ProductReviews extends HTMLElement {
  connectedCallback() {
    this.#load();
  }

  /** Every unique, non-blank variant SKU of the product, capped at the endpoint's limit. */
  get skus() {
    const skus = (this.dataset.skus || '')
      .split(',')
      .map((sku) => sku.trim())
      .filter(Boolean);
    return [...new Set(skus)].slice(0, MAX_COUNT_SKUS);
  }

  async #load() {
    const skus = this.skus;
    if (!skus.length) return this.#hide();

    await afterPageLoad();
    if (!this.isConnected) return;

    this.#loadCount();

    try {
      // Every variant's SKU has to be sent: products with many colours/sizes (e.g. 165 SKUs) often have
      // no reviews on the first few variants, which made the section disappear.
      const data = await fetchProductCards(skus);
      if (!this.isConnected) return;

      /** @type {Review[]} */
      const reviews = Array.isArray(data?.reviews) ? data.reviews : [];
      if (!reviews.length) return this.#hide();
      // Fewer than a full row: the service reviews show instead. The header link still goes to the full list.
      if (reviews.length < CARDS_SHOWN) {
        this.hidden = true;
        return;
      }
      this.#render(reviews);
    } catch (error) {
      if (error instanceof RequestError) console.warn('[product-reviews]', error.code);
      else console.warn('[product-reviews]', error);
      this.#hide();
    }
  }

  /** @param {Review[]} reviews */
  #render(reviews) {
    const list = this.querySelector('[data-reviews-list]');
    const template = this.querySelector('template[data-review-template]');
    if (!list || !(template instanceof HTMLTemplateElement)) return this.#hide();

    const productTitle = this.dataset.productTitle || '';
    const cards = reviews.slice(0, CARDS_SHOWN).map((review) => {
      const card = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
      const name = review.author?.name || 'Verified customer';

      setText(card, 'product', productTitle);
      setText(card, 'date', timeAgo(review.createdAt));
      setText(card, 'title', review.title || '');
      setReviewText(card, review.text || '');
      setText(card, 'author', name);
      return card;
    });

    list.replaceChildren(...cards);
    setupReviewsCarousel(this, list, 'product-reviews__dot');

    this.toggleAttribute('data-loaded', true);
    this.querySelector('[data-reviews-attribution]')?.removeAttribute('hidden');
  }

  /**
   * Product-level total: all variant SKUs go in one request and the backend returns the combined count.
   * Fetched once per page load; the count stays hidden when it is 0 or the request fails.
   */
  async #loadCount() {
    const skus = this.skus;
    if (!skus.length) return;

    try {
      const data = await fetchProductCount(skus);
      const count = typeof data?.count === 'number' ? data.count : 0;
      if (!count) return;

      document.querySelectorAll('[data-reviews-count]').forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        el.textContent = ` ${count} Reviews`;
        el.hidden = false;
      });
    } catch {
      // Logged by fetchProductCount.
    }
  }

  #hide() {
    this.hidden = true;
    document.querySelectorAll('[data-reviews-link]').forEach((el) => {
      if (el instanceof HTMLElement) el.hidden = true;
    });
  }
}

/**
 * @typedef {Object} ProductReview
 * @property {string} id
 * @property {number} stars
 * @property {string} text
 * @property {{ name: string | null }} author
 * @property {string | null} createdAt
 */

/**
 * Every review for the product, newest first, with numbered pages (snippets/trustpilot-product-reviews-list.liquid).
 * The first page loads when the list scrolls into view; inside a closed accordion that's when it opens.
 */
class ProductReviewsList extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  /** Page currently shown; 0 until the first page has loaded. */
  #page = 0;

  connectedCallback() {
    this.querySelector('[data-reviews-pagination]')?.addEventListener('click', this.#onPaginationClick);
    if (!this.#skus.length) return this.#hideSection();

    this.#loadSummary();

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#loadPage(1);
      },
      { rootMargin: '200px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  /** Every unique, non-blank variant SKU of the product, capped at the endpoint's limit. */
  get #skus() {
    const skus = (this.dataset.skus || '')
      .split(',')
      .map((sku) => sku.trim())
      .filter(Boolean);
    return [...new Set(skus)].slice(0, MAX_COUNT_SKUS);
  }

  /**
   * Average rating for the summary and the section's closed-state stars. Loads after the page (not lazily)
   * so a closed accordion can show the rating; a product with no reviews hides the section here.
   */
  async #loadSummary() {
    await afterPageLoad();
    if (!this.isConnected) return;

    try {
      const data = await fetchProductCount(this.#skus);
      if (!this.isConnected) return;
      if (data?.count === 0) return this.#hideSection();

      const average = typeof data?.starsAverage === 'number' ? data.starsAverage : null;
      if (average === null) return;

      const averageEl = this.querySelector('[data-reviews-average]');
      if (averageEl) averageEl.textContent = average.toFixed(1);
      this.querySelector('[data-reviews-rating]')?.removeAttribute('hidden');

      const root = this.closest('[data-reviews-section]') || this;
      root.querySelectorAll('[data-reviews-average-stars]').forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        el.style.setProperty('--stars', String(average));
        el.hidden = false;
      });
    } catch {
      // Logged by fetchProductCount. The list still works without the average.
    }
  }

  get #perPage() {
    return Number(this.dataset.perPage) || 10;
  }

  /** @param {Event} event */
  #onPaginationClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[data-page]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const page = Number(button.dataset.page);
    if (page && page !== this.#page) this.#loadPage(page);
  };

  /** @param {number} page */
  async #loadPage(page) {
    const isFirstLoad = this.#page === 0;
    this.toggleAttribute('aria-busy', true);

    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    const params = new URLSearchParams({
      skus: this.#skus.join(','),
      page: String(page),
      perPage: String(this.#perPage),
    });

    try {
      const data = await getJson(`${REVIEWS_BASE}/product-all?${params}`, controller.signal);
      const total = typeof data?.total === 'number' ? data.total : 0;
      if (!total) return this.#hideSection();

      this.#page = page;
      /** @type {ProductReview[]} */
      const reviews = Array.isArray(data?.reviews) ? data.reviews : [];
      this.#renderPage(reviews);
      this.#setTotal(total);
      // `total` sets the page count; `hasMore` guards against a total that's behind the pages.
      const pages = Math.max(Math.ceil(total / this.#perPage), data?.hasMore ? page + 1 : page);
      this.#renderPagination(pages);

      if (!isFirstLoad && this.getBoundingClientRect().top < 0) {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      // Superseded by a newer request, or the element was removed.
      if (this.#controller !== controller || !this.isConnected) return;
      if (error instanceof RequestError) console.warn('[product-reviews-list]', error.code);
      else console.warn('[product-reviews-list]', error);
      // Keep the page already shown; only hide everything if nothing loaded.
      if (isFirstLoad) this.#hideSection();
    } finally {
      clearTimeout(timeout);
      if (this.#controller === controller) {
        this.#controller = null;
        this.removeAttribute('aria-busy');
      }
    }
  }

  /**
   * Prev, numbers and next. Long ranges collapse to 1 … 4 5 6 … 12 around the current page.
   * @param {number} pages
   */
  #renderPagination(pages) {
    const nav = this.querySelector('[data-reviews-pagination]');
    if (!(nav instanceof HTMLElement)) return;
    nav.hidden = pages < 2;
    if (pages < 2) return nav.replaceChildren();

    const current = this.#page;
    /** @type {(number | null)[]} */
    const items = [];
    for (let n = 1; n <= pages; n++) {
      if (n === 1 || n === pages || Math.abs(n - current) <= 1) items.push(n);
      else if (items[items.length - 1] !== null) items.push(null);
    }

    /**
     * @param {string} label
     * @param {number} page
     * @param {string} ariaLabel
     */
    const button = (label, page, ariaLabel) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'product-reviews-list__page';
      el.textContent = label;
      el.dataset.page = String(page);
      el.setAttribute('aria-label', ariaLabel);
      if (page === current) el.setAttribute('aria-current', 'page');
      return el;
    };

    const prev = button('‹', current - 1, 'Previous page');
    prev.disabled = current === 1;
    const next = button('›', current + 1, 'Next page');
    next.disabled = current === pages;

    const numbers = items.map((n) => {
      if (n !== null) return button(String(n), n, `Page ${n}`);
      const gap = document.createElement('span');
      gap.className = 'product-reviews-list__page-gap';
      gap.textContent = '…';
      gap.setAttribute('aria-hidden', 'true');
      return gap;
    });

    nav.replaceChildren(prev, ...numbers, next);
  }

  /** @param {ProductReview[]} reviews */
  #renderPage(reviews) {
    const list = this.querySelector('[data-reviews-list]');
    const template = this.querySelector('template[data-review-template]');
    if (!list || !(template instanceof HTMLTemplateElement)) return;

    /** @type {Set<string>} */
    const seen = new Set();
    const cards = reviews
      .filter((review) => review?.id && !seen.has(review.id))
      .map((review) => {
        seen.add(review.id);
        const card = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
        const stars = Math.min(5, Math.max(1, Math.round(Number(review.stars)) || 5));

        const rating = card.querySelector('[data-review-stars]');
        if (rating instanceof HTMLElement) {
          rating.style.setProperty('--stars', String(stars));
          rating.setAttribute('aria-label', `Rated ${stars} out of 5`);
        }
        setText(card, 'date', formatDate(review.createdAt));
        setReviewText(card, review.text || '');
        setText(card, 'author', review.author?.name || 'Verified customer');
        return card;
      });

    list.replaceChildren(...cards);
  }

  /** @param {number} total */
  #setTotal(total) {
    const totalEl = this.querySelector('[data-reviews-total]');
    if (totalEl) totalEl.textContent = `${total} review${total === 1 ? '' : 's'}`;
    this.querySelector('[data-reviews-summary]')?.removeAttribute('hidden');
  }

  #hideSection() {
    const section = this.closest('[data-reviews-section]') || this;
    if (section instanceof HTMLElement) section.hidden = true;
  }
}

/**
 * Mobile review carousel (the arrows and dots are hidden from md up): the list scroll-snaps one card at a time,
 * the chevrons step through it (wrapping at the ends) and the dots follow the card in view.
 * Used by the PDP product reviews and the home page service reviews.
 * @param {HTMLElement} root holds the [data-reviews-prev] / [data-reviews-next] buttons and [data-reviews-dots]
 * @param {Element} list the scrolling list, one card per child
 * @param {string} dotClass class for the generated dot buttons
 */
function setupReviewsCarousel(root, list, dotClass) {
  const cards = [...list.children];
  const dots = root.querySelector('[data-reviews-dots]');
  if (!(list instanceof HTMLElement) || cards.length < 2) return;

  // Card width plus the gap between cards.
  const step = () => /** @type {HTMLElement} */ (cards[1]).offsetLeft - /** @type {HTMLElement} */ (cards[0]).offsetLeft;
  // A hidden list has no layout (step 0), which would give NaN; treat it as the first card.
  const current = () => {
    const width = step();
    return width ? Math.round(list.scrollLeft / width) : 0;
  };
  /** @param {number} index */
  const goTo = (index) => {
    const wrapped = (index + cards.length) % cards.length;
    list.scrollTo({ left: wrapped * step(), behavior: 'smooth' });
  };

  root.querySelector('[data-reviews-prev]')?.addEventListener('click', () => goTo(current() - 1));
  root.querySelector('[data-reviews-next]')?.addEventListener('click', () => goTo(current() + 1));

  if (!dots) return;
  const dotButtons = cards.map((_, index) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = dotClass;
    dot.setAttribute('aria-label', `Show review ${index + 1}`);
    dot.addEventListener('click', () => goTo(index));
    return dot;
  });
  dots.replaceChildren(...dotButtons);

  // The list takes the height of the card in view, so the chevrons centre on it and the dots sit right below it
  // instead of below the longest review.
  const mobile = window.matchMedia('(max-width: 748.98px)');
  const update = () => {
    const active = Math.min(current(), cards.length - 1);
    dotButtons.forEach((dot, index) => dot.setAttribute('aria-current', String(index === active)));
    list.style.height = mobile.matches ? `${/** @type {HTMLElement} */ (cards[active]).offsetHeight}px` : '';
  };
  let frame = 0;
  const scheduleUpdate = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(update);
  };
  list.addEventListener('scroll', scheduleUpdate, { passive: true });
  mobile.addEventListener('change', scheduleUpdate);
  // "Read more" and resizes change a card's height.
  const resizeObserver = new ResizeObserver(scheduleUpdate);
  cards.forEach((card) => resizeObserver.observe(card));
  update();
}

/**
 * Plain-text only; empty values remove the element so optional fields collapse.
 * @param {DocumentFragment} card
 * @param {string} field
 * @param {string} value
 */
function setText(card, field, value) {
  const el = card.querySelector(`[data-review-${field}]`);
  if (!el) return;
  if (value) el.textContent = value;
  else el.remove();
}

// Longer review texts are cut here with a "Read more" toggle, like the product description.
const REVIEW_TEXT_LIMIT = 250;

/**
 * Plain-text review body. Long texts are shortened at a word boundary with a "Read more" / "Read less" button.
 * @param {DocumentFragment} card
 * @param {string} text
 */
function setReviewText(card, text) {
  const el = card.querySelector('[data-review-text]');
  if (!el) return;
  if (!text) return el.remove();
  if (text.length <= REVIEW_TEXT_LIMIT) {
    el.textContent = text;
    return;
  }

  const cut = text.slice(0, REVIEW_TEXT_LIMIT);
  const lastSpace = cut.lastIndexOf(' ');
  const short = `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}...`;
  el.textContent = short;

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'tw:text-sm tw:underline tw:mt-2 tw:cursor-pointer tw:text-[#949494] tw:self-start';
  button.textContent = 'Read more';
  button.setAttribute('aria-expanded', 'false');

  let expanded = false;
  button.addEventListener('click', (event) => {
    // Service review cards are links to Trustpilot; the toggle shouldn't follow them.
    event.preventDefault();
    event.stopPropagation();
    expanded = !expanded;
    el.textContent = expanded ? text : short;
    button.textContent = expanded ? 'Read less' : 'Read more';
    button.setAttribute('aria-expanded', String(expanded));
  });

  el.after(button);
}

const SERVICE_REVIEWS_SHOWN = 3;
// The backend drops some reviews after paging, so ask for a few extra.
const SERVICE_REVIEWS_REQUESTED = 12;

/**
 * @typedef {Object} ServiceReview
 * @property {string} id
 * @property {number} stars
 * @property {string} [title]
 * @property {string} text
 * @property {{ name: string | null }} author
 * @property {string | null} createdAt
 * @property {string} [url]
 */

/**
 * The latest Trustpilot service (company) reviews: columns on desktop, the mobile carousel below md
 * (snippets/custom-service-reviews.liquid). On the home page a skeleton holds the space, so failures hide the element.
 * With data-skus (PDP) they're a fallback: they only show when that product has fewer than CARDS_SHOWN reviews of its own.
 */
class ServiceReviews extends HTMLElement {
  connectedCallback() {
    this.#load();
  }

  async #load() {
    await afterPageLoad();
    if (!this.isConnected) return;

    const skus = [...new Set((this.dataset.skus || '').split(',').map((sku) => sku.trim()).filter(Boolean))].slice(
      0,
      MAX_COUNT_SKUS
    );

    try {
      if (skus.length) {
        const data = await fetchProductCards(skus);
        const count = Array.isArray(data?.reviews) ? data.reviews.length : 0;
        if (count >= CARDS_SHOWN) return;
      }

      const params = new URLSearchParams({ page: '1', perPage: String(SERVICE_REVIEWS_REQUESTED) });
      const data = await fetchShared(`${REVIEWS_BASE}/latest?${params}`);
      if (!this.isConnected) return;

      /** @type {ServiceReview[]} */
      const reviews = (Array.isArray(data?.reviews) ? data.reviews : []).slice(0, SERVICE_REVIEWS_SHOWN);
      if (reviews.length) this.#render(reviews);
      else this.hidden = true;
    } catch (error) {
      if (error instanceof RequestError) console.warn('[service-reviews]', error.code);
      this.hidden = true;
    }
  }

  /** @param {ServiceReview[]} reviews */
  #render(reviews) {
    const list = this.querySelector('[data-service-reviews-list]');
    const template = this.querySelector('template[data-service-review-template]');
    if (!list || !(template instanceof HTMLTemplateElement)) return;

    const slides = reviews.map((review) => {
      const slide = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
      const name = review.author?.name || 'Verified customer';
      const stars = Math.min(5, Math.max(1, Math.round(Number(review.stars)) || 5));

      const link = slide.querySelector('[data-review-link]');
      if (link instanceof HTMLAnchorElement) {
        if (review.url) link.href = review.url;
        link.setAttribute('aria-label', `Read review by ${name} on Trustpilot`);
      }
      const rating = slide.querySelector('[data-review-stars]');
      if (rating instanceof HTMLElement) {
        rating.style.setProperty('--stars', String(stars));
        rating.setAttribute('aria-label', `Rated ${stars} out of 5`);
      }
      setText(slide, 'title', review.title || '');
      setText(slide, 'date', timeAgo(review.createdAt));
      setReviewText(slide, review.text || '');
      setText(slide, 'author', name);
      return slide;
    });

    list.replaceChildren(...slides);
    // The PDP fallback starts hidden: show it first so the carousel can measure its cards.
    this.hidden = false;
    setupReviewsCarousel(this, list, 'service-reviews-columns__dot');
    this.toggleAttribute('data-loaded', true);
  }
}

if (!customElements.get('service-reviews')) {
  customElements.define('service-reviews', ServiceReviews);
}

if (!customElements.get('product-reviews')) {
  customElements.define('product-reviews', ProductReviews);
}

if (!customElements.get('product-reviews-list')) {
  customElements.define('product-reviews-list', ProductReviewsList);
}
